import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { CHECKOUT_VERSION_FIELD, FREE_SHIPPING_ELIGIBILITY_FIELD, SHIPPING_AB_TESTING_FORM_FIELD_ID } from '@/services/Configuration'
import { error, log } from '@/services/Log'
import type { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import type {
  StandardAddress, StandardOrder, StandardShipment,
} from '@/types/ShopFront/CheckoutStandards'

const customFieldCheckoutVersion = {
  fieldId: CHECKOUT_VERSION_FIELD,
  fieldValue: '1',
}

const customFieldFreeShipping = (enabled: boolean) => ({
  fieldId: FREE_SHIPPING_ELIGIBILITY_FIELD,
  fieldValue: enabled ? 'true' : 'false',
})

export type NonBopusConsignmentCreator = (
  input: { order: StandardOrder, address: StandardAddress },
) => Promise<{
  success: boolean,
  newOrder: StandardOrder,
}>

export const createNonBopusConsignments: NonBopusConsignmentCreator = async ({
  order,
  address,
}) => {
  try {
    log('createNonBopusConsignments start', { order, address })
    const [
      { default: Axios },
      { standardizeOrderFromResponse },
      { standardizeAddressForBigC },
      { assertBCCheckout },
      { checkIfFreeShippingEnabled },
      { getVWOExperiment },
    ] = await allPromisesWithRetries(() => [
      import('axios'),
      import('@/services/Standardizers/standardizeOrderFromResponse'),
      import('@/services/Standardizers/standardizeAddressForBigC'),
      import('@/types/BigCommerce/BCCheckout'),
      import('@/helpers/checkIfFreeShippingEnabled'),
      import('@/helpers/getVWOExperiment'),
    ])
    let newOrder = { ...order }
    let success = true
    // Filter BOPUS shipment that has a matching lineItem ID
    const isBopusShipmentItemOwner = (lineItemID: string) => ({
      isBopus,
      lineItemIds,
    }: StandardShipment) => (
      isBopus && lineItemIds.indexOf(lineItemID) >= 0
    )
    const nonBopusLineItems = order?.cart?.lineItems.filter(({ id: lineItemID }) => (
      // Getting Items that are not part of a BOPUS consignment
      !order?.shipments.filter(isBopusShipmentItemOwner(lineItemID)).length
    ))

    // We need to handle furniture and non-furniture consignments separately
    // so we get the correct shipping options
    const lineItemTypeGroups = [
      // isFurniture is parsed from the checkout cart object via item per item API requests
      nonBopusLineItems.filter((lineItem) => !lineItem.isFurniture),
      nonBopusLineItems.filter((lineItem) => lineItem.isFurniture),
    ].filter((group) => group.length)

    const [
      isFreeShippingEnabled,
      shippingExperimentResult,
    ] = await Promise.all([
      checkIfFreeShippingEnabled(),
      getVWOExperiment('shippingRateLiveCap'),
    ])

    const shippingAddress = standardizeAddressForBigC({
      ...address,
      email: address?.email || order?.cart?.email,
      customFields: [...(address?.customFields || []),
        customFieldCheckoutVersion,
        customFieldFreeShipping(isFreeShippingEnabled),
        {
          fieldId: SHIPPING_AB_TESTING_FORM_FIELD_ID,
          fieldValue: shippingExperimentResult ? 'true' : 'false',
        },
      ],
    })

    // New Orders will be created and the previous ones will disappear from bigC automatically
    const payload = lineItemTypeGroups.map((shipment) => ({
      shippingAddress,
      lineItems: shipment.map(({ id: itemId, quantity }) => ({ itemId, quantity })),
    }))
    if (payload.length) {
      // Adds a new Consignment to Checkout. - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-consignments/checkoutsconsignmentsbycheckoutidpost
      try {
        const { data, status } = await Axios.post<BCCheckoutType>(`/api/storefront/checkouts/${order?.cart?.id}/consignments?include=consignments.availableShippingOptions`, payload)
        assertBCCheckout(data)
        success = success && status === 200
        newOrder = await standardizeOrderFromResponse({ bigcResponse: data, order })
      } catch (err) {
        error(`updateShippingAddress shipping option selection FAILED ${String(err)}`, {
          payload,
          order,
          newOrder,
        })
        return { success: false, newOrder: { ...order } }
      }
    }
    return { newOrder, success }
  } catch (err) {
    error(`updateShippingAddress shipping option selection FAILED ${String(err)}`)
    return { newOrder: { ...order }, success: false }
  }
}

export default createNonBopusConsignments
